/**
 * @file Controlled Drugs frontend utility helpers.
 */

import {
    isDrugsAndLeaveActionLocked,
} from "../../shared/utils/actionLock.js";

/**
 * Returns whether the Drugs UI is temporarily locked for maintenance.
 * This is a frontend-only precautionary lock.
 * @returns {boolean}
 */
export const isDrugsUiLocked = isDrugsAndLeaveActionLocked;

/**
 * Rounds a number to a specified number of decimal places.
 * Used for precision handling in drug calculations to avoid floating-point errors.
 * @param {number} value - The number to round.
 * @param {number} [decimals=4] - The number of decimal places (default: 4).
 * @returns {number} The rounded number.
 */
export function roundToPrecision(value, decimals = 4) {
    if ((value === null || value === undefined) || isNaN(value)) {return 0;}
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
}

/**
 * Sorts drugs by their admin-configured order field, with fallback to name.
 * Respects the order field set in admin settings (similar to status ordering).
 * @param {Array<object>} drugs - Array of drug objects from drug_inventory.
 * @returns {Array<object>} Sorted array of drugs.
 */
export function sortDrugsByOrder(drugs) {
    if (!Array.isArray(drugs)) {return [];}
    return [...drugs].sort((a, b) => {
        // First sort by order field (if exists)
        const aOrder = a.order !== undefined && a.order !== null ? a.order : 9999;
        const bOrder = b.order !== undefined && b.order !== null ? b.order : 9999;
        if (aOrder !== bOrder) {
            return aOrder - bOrder;
        }
        // If order is same, fallback to name sorting
        const aName = (a.shortName || a.name || '').toLowerCase();
        const bName = (b.shortName || b.name || '').toLowerCase();
        return aName.localeCompare(bName);
    });
}

/**
 * Sorts locations by their admin-configured order field, with fallback to name.
 * Respects the order field set in admin settings (similar to drug ordering).
 * @param {Array<object>} locations - Array of location objects from config.drugs.locations.
 * @returns {Array<object>} Sorted array of locations.
 */
export function sortLocationsByOrder(locations) {
    if (!Array.isArray(locations)) {return [];}
    return [...locations].sort((a, b) => {
        // First sort by order field (if exists)
        const aOrder = a.order !== undefined && a.order !== null ? a.order : 9999;
        const bOrder = b.order !== undefined && b.order !== null ? b.order : 9999;
        if (aOrder !== bOrder) {
            return aOrder - bOrder;
        }
        // If order is same, fallback to name sorting
        const aName = (a.name || a.id || '').toLowerCase();
        const bName = (b.name || b.id || '').toLowerCase();
        return aName.localeCompare(bName);
    });
}

/**
 * Sorts users by their drug tracker sort order (drugSortOrder), then alphabetically by name.
 * Uses 999 as default for users without a sort order.
 * @param {Array<object>} users - Array of user objects.
 * @returns {Array<object>} Sorted array of users.
 */
export function sortUsersByDrugOrder(users) {
    if (!Array.isArray(users)) {return [];}
    return [...users].sort((a, b) => {
        const orderA = a.drugSortOrder !== undefined && a.drugSortOrder !== null ? Number(a.drugSortOrder) : 999;
        const orderB = b.drugSortOrder !== undefined && b.drugSortOrder !== null ? Number(b.drugSortOrder) : 999;

        if (orderA !== orderB) {
            return orderA - orderB;
        }
        return (a.name || '').localeCompare(b.name || '');
    });
}
